import { format, addDays } from "date-fns";
import { supabase } from "@/lib/supabase";

export interface WorkTimeRules {
  maxShiftHours: number;
  minRestHours: number;
  maxCumulativeHours: number;
  resetBreakHours: number;
}

export const DEFAULT_WORK_TIME_RULES: WorkTimeRules = {
  maxShiftHours: 14,
  minRestHours: 10,
  maxCumulativeHours: 70,
  resetBreakHours: 24,
};

const SETTING_NAME_BY_RULE: Record<keyof WorkTimeRules, string> = {
  maxShiftHours: "work_time_max_shift_hours",
  minRestHours: "work_time_min_rest_hours",
  maxCumulativeHours: "work_time_max_cumulative_hours",
  resetBreakHours: "work_time_reset_break_hours",
};

export async function loadWorkTimeRules(): Promise<WorkTimeRules> {
  const { data, error } = await supabase
    .from("settings")
    .select("name, value")
    .in("name", Object.values(SETTING_NAME_BY_RULE));

  if (error || !data) return DEFAULT_WORK_TIME_RULES;

  const byName = new Map(data.map((row) => [row.name, row.value]));
  const rules = { ...DEFAULT_WORK_TIME_RULES };
  (Object.keys(SETTING_NAME_BY_RULE) as (keyof WorkTimeRules)[]).forEach((key) => {
    const raw = byName.get(SETTING_NAME_BY_RULE[key]);
    const n = raw != null ? parseFloat(String(raw)) : NaN;
    if (!Number.isNaN(n) && n > 0) rules[key] = n;
  });
  return rules;
}

export interface ShiftSpan {
  id?: string;
  shift_date: string; // yyyy-MM-dd
  start_hhmm: string;
  end_hhmm: string;
  /** Actual worked hours (excludes break time) -- used for the cumulative-hours rule. */
  worked_hours: number;
  /** From roster_shift_types -- false for Leave/Sick/Training/Dispatch etc.
   *  A candidate with this false skips all three rules entirely (see
   *  checkWorkTimeRules); omitted/true is treated as a normal work shift. */
  counts_as_worked_hours?: boolean;
}

export type WorkTimeRuleKey = "max_shift_length" | "min_rest" | "max_cumulative";

export interface WorkTimeViolation {
  rule: WorkTimeRuleKey;
  message: string;
}

// Wide enough to reliably find a genuine reset break (70h max at ~10h/day
// with 10h rest between shifts works out to well under 2 calendar weeks),
// with margin for a slightly-longer-than-typical stretch.
export const LOOKBACK_DAYS = 21;
export const LOOKAHEAD_DAYS = 3;

function toDate(dateStr: string, hhmm: string): Date {
  return new Date(`${dateStr}T${hhmm}:00`);
}

function shiftBounds(s: ShiftSpan): { start: Date; end: Date } {
  const start = toDate(s.shift_date, s.start_hhmm);
  let end = toDate(s.shift_date, s.end_hhmm);
  if (end <= start) end = new Date(end.getTime() + 24 * 60 * 60 * 1000); // overnight wrap
  return { start, end };
}

const round1 = (n: number) => Math.round(n * 10) / 10;

interface DutyPeriod {
  start: Date;
  end: Date;
  shifts: ShiftSpan[];
}

/**
 * Groups shifts into duty periods: a gap shorter than minRestHours isn't a
 * real rest break (that's the whole point of the setting), so it doesn't
 * start a new duty period -- it's a split shift with a meal/rest break in
 * the middle, and the two (or more) bookings are really one continuous span
 * for max-shift-length purposes. Only a gap of minRestHours or more counts
 * as a genuine break between duty periods.
 */
function groupIntoDutyPeriods(
  items: { s: ShiftSpan; start: Date; end: Date }[],
  minRestHours: number
): DutyPeriod[] {
  const sorted = [...items].sort((a, b) => a.start.getTime() - b.start.getTime());
  const groups: DutyPeriod[] = [];
  for (const item of sorted) {
    const last = groups[groups.length - 1];
    const gapHours = last ? (item.start.getTime() - last.end.getTime()) / (1000 * 60 * 60) : Infinity;
    if (last && gapHours < minRestHours - 1e-9) {
      if (item.end > last.end) last.end = item.end;
      last.shifts.push(item.s);
    } else {
      groups.push({ start: item.start, end: item.end, shifts: [item.s] });
    }
  }
  return groups;
}

/**
 * Fetches this driver's other shifts within an explicit date range. Excludes
 * cancelled shifts, non-working shifts (Leave/Sick/Training/Dispatch etc. --
 * anything with roster_shift_types.counts_as_worked_hours = false -- these
 * aren't real work, so they must never contribute to duty-period grouping,
 * min-rest, or cumulative-hours) and, when editing existing shift(s), those
 * shifts' own current rows (pass their id(s) as excludeShiftIds so they
 * aren't double-counted against the new candidate versions of themselves).
 */
export async function loadDriverShiftsInRange(
  driverName: string,
  fromDateStr: string,
  toDateStr: string,
  excludeShiftIds?: string[]
): Promise<ShiftSpan[]> {
  const { data, error } = await supabase
    .from("vw_roster_shifts_enriched")
    .select("id, shift_date, start_hhmm, end_hhmm, worked_hours, status, counts_as_worked_hours")
    .eq("driver_name", driverName)
    .neq("status", "cancelled")
    .gte("shift_date", fromDateStr)
    .lte("shift_date", toDateStr);

  if (error) throw error;

  const excludeSet = new Set(excludeShiftIds || []);
  return (data || [])
    .filter((s) => !excludeSet.has(s.id) && s.counts_as_worked_hours !== false)
    .map((s) => ({
      id: s.id,
      shift_date: s.shift_date,
      start_hhmm: s.start_hhmm,
      end_hhmm: s.end_hhmm,
      worked_hours: s.worked_hours || 0,
    }));
}

/**
 * Fetches this driver's other shifts around a single candidate date, wide
 * enough (see LOOKBACK_DAYS/LOOKAHEAD_DAYS) to run all three work-time-rule
 * checks for that one shift.
 */
export async function loadDriverShiftsForValidation(
  driverName: string,
  aroundDateStr: string,
  excludeShiftId?: string
): Promise<ShiftSpan[]> {
  const around = new Date(`${aroundDateStr}T00:00:00`);
  const from = format(addDays(around, -LOOKBACK_DAYS), "yyyy-MM-dd");
  const to = format(addDays(around, LOOKAHEAD_DAYS), "yyyy-MM-dd");
  return loadDriverShiftsInRange(driverName, from, to, excludeShiftId ? [excludeShiftId] : undefined);
}

/**
 * Checks one candidate shift against all three work-time rules. `driverShifts`
 * must be that same driver's OTHER shifts (see loadDriverShiftsForValidation)
 * -- doesn't include the candidate itself. Pure/synchronous so the same logic
 * drives both the hard-block-on-save check and the read-only pulsing flag.
 *
 * Shifts separated by less than minRestHours are treated as one duty period
 * (see groupIntoDutyPeriods) -- a split shift with a short break in the
 * middle is checked as a single span against maxShiftHours, not flagged for
 * insufficient rest between what are really two halves of the same day.
 */
export function checkWorkTimeRules(
  candidate: ShiftSpan,
  driverShifts: ShiftSpan[],
  rules: WorkTimeRules
): WorkTimeViolation[] {
  // Leave/Sick/Training/Dispatch etc. aren't work -- these rules don't apply
  // to them at all, regardless of what else is on the roster that day.
  if (candidate.counts_as_worked_hours === false) return [];

  const violations: WorkTimeViolation[] = [];

  // Group this driver's shifts (plus the candidate) into duty periods first --
  // a gap under minRestHours doesn't count as real rest, so those shifts are
  // one continuous duty period, not separate shifts needing rest between them.
  const allItems = [...driverShifts, candidate].map((s) => ({ s, ...shiftBounds(s) }));
  const groups = groupIntoDutyPeriods(allItems, rules.minRestHours);
  const groupIndex = groups.findIndex((g) => g.shifts.includes(candidate));
  const candidateGroup = groups[groupIndex];

  // 1. Maximum shift length -- the whole duty period's span (start of its
  // first shift to end of its last), breaks included, not just this one
  // booking's own span.
  const spanHours = (candidateGroup.end.getTime() - candidateGroup.start.getTime()) / (1000 * 60 * 60);
  if (spanHours > rules.maxShiftHours + 1e-9) {
    const combinedNote = candidateGroup.shifts.length > 1 ? " (combined with a nearby split-shift booking)" : "";
    violations.push({
      rule: "max_shift_length",
      message: `Shift is ${round1(spanHours)}h long${combinedNote} -- exceeds the ${rules.maxShiftHours}h maximum shift length.`,
    });
  }

  // 2. Minimum rest before and after this duty period (not before/after every
  // individual booking inside it -- a short meal break in the middle of a
  // split shift isn't a min-rest violation, it's covered by rule 1 above).
  const prevGroup = groupIndex > 0 ? groups[groupIndex - 1] : null;
  const nextGroup = groupIndex < groups.length - 1 ? groups[groupIndex + 1] : null;
  if (prevGroup) {
    const restBefore = (candidateGroup.start.getTime() - prevGroup.end.getTime()) / (1000 * 60 * 60);
    if (restBefore < rules.minRestHours - 1e-9) {
      violations.push({
        rule: "min_rest",
        message: `Only ${round1(restBefore)}h rest since the previous shift ended -- needs at least ${rules.minRestHours}h.`,
      });
    }
  }
  if (nextGroup) {
    const restAfter = (nextGroup.start.getTime() - candidateGroup.end.getTime()) / (1000 * 60 * 60);
    if (restAfter < rules.minRestHours - 1e-9) {
      violations.push({
        rule: "min_rest",
        message: `Only ${round1(restAfter)}h rest before the next shift starts -- needs at least ${rules.minRestHours}h.`,
      });
    }
  }

  // 3. Rolling cumulative work hours, resetting after a break >= resetBreakHours.
  const combined = [...driverShifts, candidate]
    .map((s) => ({ s, ...shiftBounds(s) }))
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  let cumulative = 0;
  let prevEndForCycle: Date | null = null;
  for (const item of combined) {
    if (prevEndForCycle) {
      const gapHours = (item.start.getTime() - prevEndForCycle.getTime()) / (1000 * 60 * 60);
      if (gapHours >= rules.resetBreakHours - 1e-9) cumulative = 0;
    }
    cumulative += item.s.worked_hours;
    prevEndForCycle = item.end;

    if (item.s === candidate && cumulative > rules.maxCumulativeHours + 1e-9) {
      violations.push({
        rule: "max_cumulative",
        message: `This shift brings cumulative work hours to ${round1(cumulative)}h since the last ${rules.resetBreakHours}h+ break -- exceeds the ${rules.maxCumulativeHours}h maximum. A ${rules.resetBreakHours}h break is needed first.`,
      });
    }
  }

  return violations;
}

/** Convenience wrapper for a single new/edited shift: fetches history and checks it in one call. */
export async function validateShift(
  driverName: string,
  candidate: ShiftSpan,
  rules: WorkTimeRules,
  excludeShiftId?: string
): Promise<WorkTimeViolation[]> {
  const history = await loadDriverShiftsForValidation(driverName, candidate.shift_date, excludeShiftId);
  return checkWorkTimeRules(candidate, history, rules);
}

export interface BatchValidationResult {
  candidate: ShiftSpan;
  violations: WorkTimeViolation[];
}

/**
 * Validates a whole batch of candidate dates for the same driver (e.g. a
 * repeating shift series) in one go. Checks each date in chronological order
 * against the driver's real history PLUS every earlier candidate in the same
 * batch -- otherwise a multi-day repeat series could never trip the
 * cumulative-hours rule, since none of its own shifts would exist yet when
 * checking any single one in isolation.
 */
export async function validateShiftBatch(
  driverName: string,
  candidates: ShiftSpan[],
  rules: WorkTimeRules,
  excludeShiftIds?: string[]
): Promise<BatchValidationResult[]> {
  if (candidates.length === 0) return [];

  const sorted = [...candidates].sort((a, b) => (a.shift_date + a.start_hhmm).localeCompare(b.shift_date + b.start_hhmm));
  const earliest = new Date(`${sorted[0].shift_date}T00:00:00`);
  const latest = new Date(`${sorted[sorted.length - 1].shift_date}T00:00:00`);
  const from = format(addDays(earliest, -LOOKBACK_DAYS), "yyyy-MM-dd");
  const to = format(addDays(latest, LOOKAHEAD_DAYS), "yyyy-MM-dd");

  let runningShifts = await loadDriverShiftsInRange(driverName, from, to, excludeShiftIds);

  const results: BatchValidationResult[] = [];
  for (const candidate of sorted) {
    const violations = checkWorkTimeRules(candidate, runningShifts, rules);
    results.push({ candidate, violations });
    runningShifts = [...runningShifts, candidate];
  }

  return results;
}
